// Liquidität über einen frei gewählten Zeitraum — rückblickend und als Vorschau.
//
// Früher zeigte die Finanzlage nur die nächsten 90 Tage. Jetzt wählt man:
// Vorschau (30 Tage bis ein Jahr), diesen Monat, dieses oder das letzte
// Quartal, dieses Jahr, zwölf Monate, alles seit der ersten Zahlung — oder
// einen eigenen Zeitraum. Monat, Quartal und Jahr laufen bis zu ihrem Ende:
// links das, was war, rechts gestrichelt das, was kommt.
import { useEffect, useMemo, useState } from 'react';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { euro, datum } from '../../../gemeinsam/lib/daten';
import { Feld, Hinweis, Karte, KartenKopf, Spinner } from '../../../gemeinsam/komponenten/Basis';

type Posten = { datum: string; text: string; betrag: number; art: string; geplant: boolean };
type Verlauf = {
  von: string; bis: string; heute: string; ohneKonto: boolean; ausgeduennt: number | null;
  punkte: Array<{ datum: string; stand: number }>;
  anfang: { datum: string; stand: number } | null; ende: { datum: string; stand: number } | null;
  tiefpunkt: { datum: string; stand: number } | null; hoechststand: { datum: string; stand: number } | null;
  einnahmen: number; ausgaben: number; geplantEin: number; geplantAus: number;
  posten: Posten[]; nichtEingeplant: { betrag: number; anzahl: number };
};

type Wahl = 'v30' | 'v90' | 'v180' | 'v365' | 'monat' | 'quartal' | 'vorquartal' | 'jahr' | 'zwoelf' | 'gesamt' | 'eigen';
const VORSCHAU: Array<[Wahl, string]> = [['v30', '30 Tage'], ['v90', '90 Tage'], ['v180', '6 Monate'], ['v365', '1 Jahr']];
const RUECKBLICK: Array<[Wahl, string]> = [
  ['monat', 'Dieser Monat'], ['quartal', 'Dieses Quartal'], ['vorquartal', 'Letztes Quartal'],
  ['jahr', 'Dieses Jahr'], ['zwoelf', '12 Monate'], ['gesamt', 'Gesamt'], ['eigen', 'Eigener Zeitraum'],
];
const SPEICHER = 'lp_finanzlage_zeitraum';

const tag = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const plus = (d: Date, n: number) => { const x = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12); x.setDate(x.getDate() + n); return x; };

/** Von–bis zu einer Wahl; null bei „gesamt" (rechnet der Server). */
function zeitraum(w: Wahl, eigen: { von: string; bis: string }): { von: string; bis: string } | null {
  const h = new Date();
  const q = Math.floor(h.getMonth() / 3);
  switch (w) {
    case 'v30': return { von: tag(h), bis: tag(plus(h, 30)) };
    case 'v90': return { von: tag(h), bis: tag(plus(h, 90)) };
    case 'v180': return { von: tag(h), bis: tag(plus(h, 182)) };
    case 'v365': return { von: tag(h), bis: tag(plus(h, 365)) };
    case 'monat': return { von: tag(new Date(h.getFullYear(), h.getMonth(), 1)), bis: tag(new Date(h.getFullYear(), h.getMonth() + 1, 0)) };
    case 'quartal': return { von: tag(new Date(h.getFullYear(), q * 3, 1)), bis: tag(new Date(h.getFullYear(), q * 3 + 3, 0)) };
    case 'vorquartal': return { von: tag(new Date(h.getFullYear(), q * 3 - 3, 1)), bis: tag(new Date(h.getFullYear(), q * 3, 0)) };
    case 'jahr': return { von: tag(new Date(h.getFullYear(), 0, 1)), bis: tag(new Date(h.getFullYear(), 11, 31)) };
    case 'zwoelf': return { von: tag(plus(h, -365)), bis: tag(h) };
    case 'gesamt': return null;
    case 'eigen': return eigen;
  }
}

export default function LiquiditaetsVerlauf() {
  const [wahl, setWahlRoh] = useState<Wahl>(() => {
    try { return (localStorage.getItem(SPEICHER) as Wahl) || 'v90'; } catch { return 'v90'; }
  });
  const setWahl = (w: Wahl) => { setWahlRoh(w); try { localStorage.setItem(SPEICHER, w); } catch { /* egal */ } };
  const [eigen, setEigen] = useState(() => ({ von: tag(new Date(new Date().getFullYear(), 0, 1)), bis: tag(new Date()) }));
  const [daten, setDaten] = useState<Verlauf | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [alle, setAlle] = useState(false);

  const z = zeitraum(wahl, eigen);
  const abfrage = z ? `von=${z.von}&bis=${z.bis}` : 'zeitraum=gesamt';
  const eigenUngueltig = wahl === 'eigen' && (!eigen.von || !eigen.bis || eigen.von > eigen.bis);

  useEffect(() => {
    if (eigenUngueltig) return;
    const steuerung = new AbortController();
    setLaedt(true); setFehler(null); setAlle(false);
    api<Verlauf>(`/accounting/finanzlage/verlauf?${abfrage}`, { signal: steuerung.signal })
      .then(setDaten)
      .catch(e => { if ((e as Error).name !== 'AbortError') setFehler(e instanceof ApiFehler ? e.message : 'Der Verlauf konnte nicht geladen werden.'); })
      .finally(() => setLaedt(false));
    return () => steuerung.abort();
  }, [abfrage, eigenUngueltig]);

  const posten = useMemo(() => {
    if (!daten) return [];
    // Reine Vorschau liest sich besser der Reihe nach, Rückblick neueste zuerst.
    return daten.posten.every(p => p.geplant) ? [...daten.posten].reverse() : daten.posten;
  }, [daten]);

  const knopf = (w: Wahl, text: string) => (
    <button key={w} type="button" onClick={() => setWahl(w)} aria-pressed={wahl === w}
            className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition ${wahl === w
              ? 'bg-marke-600 text-white'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'}`}>
      {text}
    </button>
  );

  const titel = wahl.startsWith('v') ? 'Vorschau' : 'Verlauf';
  const mitVorschau = !!daten && daten.bis > daten.heute;
  const mitVergangenheit = !!daten && daten.von < daten.heute;

  return (
    <Karte>
      <KartenKopf titel={`Liquidität · ${titel}`}
                  hinweis={daten?.ohneKonto
                    ? 'Ohne Kontostand zeigt die Kurve die Veränderung — heute ist null.'
                    : 'Vergangenheit aus den gebuchten Zahlungen, Zukunft aus fälligen Rechnungen, festen Zahlungen und Umsatzsteuer.'} />
      <div className="space-y-2 border-b border-slate-200 px-5 py-3 dark:border-slate-800">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="me-1 text-xs font-medium text-slate-500 dark:text-slate-400">Vorschau</span>
          {VORSCHAU.map(([w, t]) => knopf(w, t))}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="me-1 text-xs font-medium text-slate-500 dark:text-slate-400">Zeitraum</span>
          {RUECKBLICK.map(([w, t]) => knopf(w, t))}
        </div>
        {wahl === 'eigen' && (
          <div className="flex flex-wrap items-end gap-3 pt-1">
            <div className="w-40"><Feld label="Von" type="date" value={eigen.von} onChange={e => setEigen(x => ({ ...x, von: e.target.value }))} /></div>
            <div className="w-40"><Feld label="Bis" type="date" value={eigen.bis} onChange={e => setEigen(x => ({ ...x, bis: e.target.value }))} /></div>
            {eigenUngueltig && <p className="pb-2 text-xs text-red-600 dark:text-red-400">Der Anfang muss vor dem Ende liegen.</p>}
          </div>
        )}
      </div>

      <div className="p-5">
        {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
        {!daten ? <div className="flex justify-center py-12"><Spinner /></div> : (
          <div className={laedt ? 'opacity-60 transition-opacity' : ''}>
            <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
              {datum(daten.von)} – {datum(daten.bis)}
              {daten.ausgeduennt && ` · je ${daten.ausgeduennt} Tage ein Punkt`}
            </p>
            <Kurve d={daten} />
            {mitVergangenheit && mitVorschau && (
              <p className="mt-2 flex gap-4 text-[11px] text-slate-500 dark:text-slate-400">
                <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-5 bg-marke-600 dark:bg-marke-400" />gebucht</span>
                <span className="inline-flex items-center gap-1.5"><span className="h-0 w-5 border-t-2 border-dashed border-marke-600 dark:border-marke-400" />Vorschau</span>
              </p>
            )}

            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Zahl titel={`Stand ${datum(daten.anfang?.datum)}`} wert={daten.anfang?.stand ?? 0} />
              <Zahl titel={`Stand ${datum(daten.ende?.datum)}`} wert={daten.ende?.stand ?? 0} />
              <Zahl titel={mitVorschau && !mitVergangenheit ? 'Eingang geplant' : 'Eingang'}
                    wert={daten.einnahmen + daten.geplantEin} ton="plus"
                    zusatz={mitVergangenheit && mitVorschau && daten.geplantEin ? `davon ${euro(daten.geplantEin)} geplant` : undefined} />
              <Zahl titel={mitVorschau && !mitVergangenheit ? 'Ausgang geplant' : 'Ausgang'}
                    wert={-(daten.ausgaben + daten.geplantAus)}
                    zusatz={mitVergangenheit && mitVorschau && daten.geplantAus ? `davon ${euro(daten.geplantAus)} geplant` : undefined} />
            </div>
            {daten.tiefpunkt && daten.hoechststand && (
              <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                Tiefster Stand <strong className={daten.tiefpunkt.stand < 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-700 dark:text-slate-300'}>{euro(daten.tiefpunkt.stand)}</strong> am {datum(daten.tiefpunkt.datum)}
                {' · '}höchster <strong className="text-slate-700 dark:text-slate-300">{euro(daten.hoechststand.stand)}</strong> am {datum(daten.hoechststand.datum)}
              </p>
            )}
            {mitVorschau && daten.nichtEingeplant.anzahl > 0 && (
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                Nicht eingeplant: {daten.nichtEingeplant.anzahl} überfällige Rechnung(en) über {euro(daten.nichtEingeplant.betrag)} —
                kommt das Geld, ist es ein Plus.
              </p>
            )}
          </div>
        )}
      </div>

      {!!posten.length && (
        <>
          <ul className="divide-y divide-slate-200 border-t border-slate-200 text-sm dark:divide-slate-800 dark:border-slate-800">
            {(alle ? posten : posten.slice(0, 10)).map((p, i) => (
              <li key={i} className="flex items-center gap-3 px-5 py-2.5">
                <span className="w-20 shrink-0 tabular-nums text-slate-500 dark:text-slate-400">{datum(p.datum)}</span>
                <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-300">
                  {p.text}
                  {p.geplant && <span className="ms-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-400">geplant</span>}
                </span>
                <span className={`shrink-0 font-medium tabular-nums ${p.betrag >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-900 dark:text-slate-100'}`}>
                  {p.betrag >= 0 ? '+' : '−'}{euro(Math.abs(p.betrag))}
                </span>
              </li>
            ))}
          </ul>
          {posten.length > 10 && (
            <button type="button" onClick={() => setAlle(x => !x)}
                    className="w-full border-t border-slate-200 py-2.5 text-sm font-medium text-marke-700 hover:bg-slate-50 dark:border-slate-800 dark:text-marke-300 dark:hover:bg-slate-800/40">
              {alle ? 'Weniger anzeigen' : `Alle ${posten.length} Zahlungen anzeigen`}
            </button>
          )}
        </>
      )}
    </Karte>
  );
}

function Zahl({ titel, wert, zusatz, ton }: { titel: string; wert: number; zusatz?: string; ton?: 'plus' }) {
  const farbe = ton === 'plus' ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-900 dark:text-slate-100';
  return (
    <div>
      <p className="text-xs text-slate-500 dark:text-slate-400">{titel}</p>
      <p className={`text-lg font-semibold tabular-nums ${farbe}`}>{euro(wert)}</p>
      {zusatz && <p className="text-[11px] text-slate-500 dark:text-slate-400">{zusatz}</p>}
    </div>
  );
}

/**
 * Liniendiagramm: gebucht durchgezogen, Vorschau gestrichelt, „heute" als
 * Markierung. Fährt man darüber, zeigt ein Fadenkreuz Tag und Stand.
 */
function Kurve({ d }: { d: Verlauf }) {
  const B = 600, H = 170;
  const [zeige, setZeige] = useState<number | null>(null);
  const g = useMemo(() => {
    const p = d.punkte;
    const werte = p.map(x => x.stand);
    const min = Math.min(0, ...werte);
    const max = Math.max(...werte, min + 1);
    const puffer = (max - min) * 0.08;
    const unten = min - puffer, oben = max + puffer;
    const x = (i: number) => (i / Math.max(1, p.length - 1)) * B;
    const y = (v: number) => H - ((v - unten) / (oben - unten)) * H;
    const hi = p.findIndex(q => q.datum >= d.heute);
    const heuteIdx = hi < 0 ? p.length - 1 : p[hi].datum === d.heute ? hi : Math.max(0, hi - 1);
    const linie = (von: number, bis: number) => p.slice(von, bis + 1)
      .map((q, k) => `${k ? 'L' : 'M'}${x(von + k).toFixed(1)},${y(q.stand).toFixed(1)}`).join(' ');
    const ganz = linie(0, p.length - 1);
    const vergangen = d.von < d.heute;
    const zukunft = d.bis > d.heute;
    return {
      x, y, min, max,
      flaeche: `${ganz} L${B},${H} L0,${H} Z`,
      ist: vergangen ? linie(0, zukunft ? heuteIdx : p.length - 1) : '',
      plan: zukunft ? linie(vergangen ? heuteIdx : 0, p.length - 1) : '',
      heuteX: vergangen && zukunft ? x(heuteIdx) : null,
      nullY: min < 0 ? y(0) : null,
    };
  }, [d]);

  if (!d.punkte.length) return <p className="py-10 text-center text-sm text-slate-500">Keine Daten im Zeitraum.</p>;
  const tief = d.tiefpunkt ? d.punkte.findIndex(p => p.datum === d.tiefpunkt!.datum) : -1;

  function bewegen(e: React.PointerEvent<SVGSVGElement>) {
    const r = e.currentTarget.getBoundingClientRect();
    const anteil = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    setZeige(Math.round(anteil * (d.punkte.length - 1)));
  }
  const aktiv = zeige !== null ? d.punkte[zeige] : null;

  return (
    <div className="relative">
      <div className="flex justify-between text-[11px] tabular-nums text-slate-400"><span>{euro(g.max)}</span></div>
      <svg viewBox={`0 0 ${B} ${H}`} preserveAspectRatio="none" className="h-44 w-full touch-none" role="img"
           aria-label={`Liquidität ${datum(d.von)} bis ${datum(d.bis)}, von ${euro(d.anfang?.stand ?? 0)} auf ${euro(d.ende?.stand ?? 0)}`}
           onPointerMove={bewegen} onPointerDown={bewegen} onPointerLeave={() => setZeige(null)}>
        <path d={g.flaeche} className="fill-marke-500/10 dark:fill-marke-400/10" />
        {g.nullY !== null && (
          <line x1="0" x2={B} y1={g.nullY} y2={g.nullY} className="stroke-red-400/70" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" />
        )}
        {g.heuteX !== null && (
          <line x1={g.heuteX} x2={g.heuteX} y1="0" y2={H} className="stroke-slate-400 dark:stroke-slate-500" strokeDasharray="2 3" vectorEffect="non-scaling-stroke" />
        )}
        {g.ist && <path d={g.ist} fill="none" className="stroke-marke-600 dark:stroke-marke-400" strokeWidth="2" vectorEffect="non-scaling-stroke" />}
        {g.plan && <path d={g.plan} fill="none" className="stroke-marke-600 dark:stroke-marke-400" strokeWidth="2" strokeDasharray="6 4" vectorEffect="non-scaling-stroke" />}
        {tief >= 0 && (
          <circle cx={g.x(tief)} cy={g.y(d.punkte[tief].stand)} r="4" className={d.punkte[tief].stand < 0 ? 'fill-red-500' : 'fill-amber-500'} />
        )}
        {aktiv && zeige !== null && (
          <>
            <line x1={g.x(zeige)} x2={g.x(zeige)} y1="0" y2={H} className="stroke-slate-500 dark:stroke-slate-300" vectorEffect="non-scaling-stroke" />
            <circle cx={g.x(zeige)} cy={g.y(aktiv.stand)} r="4" className="fill-marke-600 stroke-white dark:fill-marke-400 dark:stroke-slate-900" strokeWidth="2" />
          </>
        )}
      </svg>
      {aktiv && zeige !== null && (
        <div className="pointer-events-none absolute top-5 z-10 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs text-white shadow-lg dark:bg-slate-100 dark:text-slate-900"
             style={{ left: `${Math.min(88, Math.max(12, (zeige / Math.max(1, d.punkte.length - 1)) * 100))}%` }}>
          {datum(aktiv.datum)}{aktiv.datum > d.heute ? ' · geplant' : ''}<br /><strong className="tabular-nums">{euro(aktiv.stand)}</strong>
        </div>
      )}
      <div className="relative mt-1 h-4 text-[11px] text-slate-400">
        <span className="absolute start-0">{datum(d.von)}</span>
        {g.heuteX !== null && <span className="absolute -translate-x-1/2" style={{ left: `${(g.heuteX / B) * 100}%` }}>heute</span>}
        <span className="absolute end-0">{datum(d.bis)}</span>
      </div>
      <table className="sr-only">
        <caption>Liquidität je Tag</caption>
        <tbody>{d.punkte.map(p => <tr key={p.datum}><th>{datum(p.datum)}</th><td>{euro(p.stand)}</td></tr>)}</tbody>
      </table>
    </div>
  );
}
